import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Brand, type Campaign, type Post } from "../../lib/api";
import { getPlatformMeta } from "../../lib/platform-meta";

// ─── Types ─────────────────────────────────────────────────────────────────────
type CalendarView = "month" | "week" | "day";
type FilterMode = "all" | "scheduled" | "published";

interface DropTarget {
  date: Date;
  hour?: number; // undefined = month-day drop (opens modal); defined = time-slot drop (instant)
}

// ─── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => i);
const CARDS_PER_PAGE = 12; // 4 columns × 3 rows

// ─── Date helpers ───────────────────────────────────────────────────────────────
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfWeekSun(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
}

function getWeekDays(anchor: Date): Date[] {
  const s = startOfWeekSun(anchor);
  return Array.from(
    { length: 7 },
    (_, i) => new Date(s.getFullYear(), s.getMonth(), s.getDate() + i),
  );
}

function getMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days: Date[] = [];
  for (let i = first.getDay() - 1; i >= 0; i--)
    days.push(new Date(year, month, -i));
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  const rem = (7 - (days.length % 7)) % 7;
  for (let i = 1; i <= rem; i++) days.push(new Date(year, month + 1, i));
  return days;
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtHour(h: number): string {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
}

/** Safe local-timezone date string for <input type="date"> */
function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toTimeInputValue(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function groupByDay(posts: Post[]): Record<string, Post[]> {
  const map: Record<string, Post[]> = {};
  for (const p of posts) {
    if (!p.scheduledAt) continue;
    const key = new Date(p.scheduledAt).toDateString();
    (map[key] ??= []).push(p);
  }
  for (const arr of Object.values(map))
    arr.sort((a, b) => (a.scheduledAt ?? 0) - (b.scheduledAt ?? 0));
  return map;
}

// Derive short initials from a name for avatars / badges
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ─── EventPill ──────────────────────────────────────────────────────────────────
interface EventPillProps {
  post: Post;
  showTime?: boolean;
  brandName?: string;
  onClick: () => void;
  onRemove: () => void;
  onDragStart: () => void;
}

function EventPill({
  post,
  showTime = true,
  brandName,
  onClick,
  onRemove,
  onDragStart,
}: EventPillProps) {
  const meta = getPlatformMeta(post.platform);
  const isPublished = post.status === "published";

  return (
    <div
      draggable={!isPublished}
      onDragStart={(e) => {
        e.stopPropagation();
        onDragStart();
      }}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      className="group relative flex items-center gap-1 rounded px-1.5 py-0.5 text-white text-xs select-none transition-opacity hover:opacity-90 cursor-grab active:cursor-grabbing"
      style={{ backgroundColor: meta.color }}
      title={`${brandName ? brandName + " · " : ""}${post.copy}`}
    >
      {showTime && post.scheduledAt && (
        <span className="opacity-75 flex-shrink-0 text-[10px] font-medium">
          {fmtTime(post.scheduledAt)}
        </span>
      )}
      <span className="truncate flex-1 min-w-0">{post.copy.slice(0, 24)}</span>
      {isPublished && (
        <span className="flex-shrink-0 opacity-80 text-[10px] font-bold">
          ✓
        </span>
      )}
      {!isPublished && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="hidden group-hover:flex items-center justify-center rounded-full bg-black/25 hover:bg-black/45 flex-shrink-0 text-[10px] leading-none font-bold transition-colors"
          style={{ width: 14, height: 14 }}
          title="Unschedule"
        >
          ×
        </button>
      )}
    </div>
  );
}

// ─── ApprovedCard ───────────────────────────────────────────────────────────────
interface ApprovedCardProps {
  post: Post;
  brandName?: string;
  campaignName?: string;
  onDragStart: () => void;
  onClick: () => void;
}

function ApprovedCard({
  post,
  brandName,
  campaignName,
  onDragStart,
  onClick,
}: ApprovedCardProps) {
  const meta = getPlatformMeta(post.platform);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="card p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all select-none border-2 border-dashed border-gray-200 hover:border-brand-300 hover:scale-[1.01] flex flex-col gap-1.5"
    >
      {/* Platform row */}
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block rounded-full flex-shrink-0"
          style={{ backgroundColor: meta.color, width: 8, height: 8 }}
        />
        <span className="text-xs font-semibold" style={{ color: meta.color }}>
          {meta.shortLabel}
        </span>
        <svg
          className="ml-auto text-gray-300 flex-shrink-0"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <circle cx="3.5" cy="2.5" r="1.2" />
          <circle cx="8.5" cy="2.5" r="1.2" />
          <circle cx="3.5" cy="6" r="1.2" />
          <circle cx="8.5" cy="6" r="1.2" />
          <circle cx="3.5" cy="9.5" r="1.2" />
          <circle cx="8.5" cy="9.5" r="1.2" />
        </svg>
      </div>

      {/* Brand / Campaign */}
      {(brandName || campaignName) && (
        <div className="flex items-center gap-1 min-w-0">
          {brandName && (
            <span className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium px-1.5 py-0.5 rounded truncate max-w-[50%]">
              <span
                className="inline-block w-3 h-3 rounded-full flex-shrink-0 text-white text-[7px] font-bold flex items-center justify-center"
                style={{
                  backgroundColor: meta.color,
                  lineHeight: "12px",
                  textAlign: "center",
                }}
              >
                {initials(brandName)[0]}
              </span>
              <span className="truncate">{brandName}</span>
            </span>
          )}
          {campaignName && (
            <span className="text-[10px] text-gray-400 truncate min-w-0 italic">
              {campaignName}
            </span>
          )}
        </div>
      )}

      {/* Copy */}
      <p className="text-xs text-gray-700 line-clamp-3 leading-relaxed">
        {post.copy}
      </p>

      {/* Hashtags */}
      {post.hashtags.length > 0 && (
        <p className="text-[11px] text-gray-400 truncate">
          #{post.hashtags[0]}
          {post.hashtags.length > 1 ? ` +${post.hashtags.length - 1}` : ""}
        </p>
      )}

      {/* Image indicator */}
      {post.imageR2Key && (
        <div className="h-7 rounded bg-gray-100 flex items-center justify-center">
          <span className="text-gray-400 text-[11px]">📷 Image attached</span>
        </div>
      )}
    </div>
  );
}

// ─── ScheduleModal ──────────────────────────────────────────────────────────────
interface ScheduleModalProps {
  post: Post;
  initialDate: Date;
  initialHour?: number;
  brandName?: string;
  campaignName?: string;
  onConfirm: (ts: number) => void;
  onClose: () => void;
}

function ScheduleModal({
  post,
  initialDate,
  initialHour,
  brandName,
  campaignName,
  onConfirm,
  onClose,
}: ScheduleModalProps) {
  const meta = getPlatformMeta(post.platform);
  const [dateStr, setDateStr] = useState(toDateInputValue(initialDate));
  const defaultHour =
    initialHour ??
    (post.scheduledAt ? new Date(post.scheduledAt).getHours() : 9);
  const defaultMin = post.scheduledAt
    ? new Date(post.scheduledAt).getMinutes()
    : 0;
  const [timeStr, setTimeStr] = useState(
    `${String(defaultHour).padStart(2, "0")}:${String(defaultMin).padStart(2, "0")}`,
  );

  const handleConfirm = () => {
    const d = new Date(`${dateStr}T${timeStr}`);
    if (!isNaN(d.getTime())) onConfirm(d.getTime());
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="card p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <span
            className="inline-block rounded-full flex-shrink-0"
            style={{ backgroundColor: meta.color, width: 10, height: 10 }}
          />
          <h3 className="font-semibold text-gray-900">Schedule Post</h3>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="mb-5 p-3 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="text-xs font-medium px-1.5 py-0.5 rounded text-white"
              style={{ backgroundColor: meta.color }}
            >
              {meta.shortLabel}
            </span>
            {brandName && (
              <span className="text-xs text-gray-500 font-medium">
                {brandName}
              </span>
            )}
            {campaignName && (
              <span className="text-xs text-gray-400 italic">
                {campaignName}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 line-clamp-3">{post.copy}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              className="input"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Time</label>
            <input
              type="time"
              className="input"
              value={timeStr}
              onChange={(e) => setTimeStr(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleConfirm} className="btn-primary flex-1">
            Schedule
          </button>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditModal ──────────────────────────────────────────────────────────────────
interface EditModalProps {
  post: Post;
  brandName?: string;
  campaignName?: string;
  onSave: (ts: number) => void;
  onRemove: () => void;
  onClose: () => void;
}

function EditModal({
  post,
  brandName,
  campaignName,
  onSave,
  onRemove,
  onClose,
}: EditModalProps) {
  const meta = getPlatformMeta(post.platform);
  const d = post.scheduledAt ? new Date(post.scheduledAt) : new Date();
  const [dateStr, setDateStr] = useState(toDateInputValue(d));
  const [timeStr, setTimeStr] = useState(toTimeInputValue(d));
  const isPublished = post.status === "published";

  const handleSave = () => {
    const newDate = new Date(`${dateStr}T${timeStr}`);
    if (!isNaN(newDate.getTime())) onSave(newDate.getTime());
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="card p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <span
            className="inline-block rounded-full flex-shrink-0"
            style={{ backgroundColor: meta.color, width: 10, height: 10 }}
          />
          <h3 className="font-semibold text-gray-900">
            {isPublished ? "Published Post" : "Edit Scheduled Post"}
          </h3>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mb-5 p-3 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="text-xs font-medium px-1.5 py-0.5 rounded text-white"
              style={{ backgroundColor: meta.color }}
            >
              {meta.shortLabel}
            </span>
            {brandName && (
              <span className="text-xs text-gray-600 font-medium">
                {brandName}
              </span>
            )}
            {campaignName && (
              <span className="text-xs text-gray-400 italic">
                {campaignName}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 line-clamp-4">{post.copy}</p>
          <div className="mt-2.5">
            {isPublished ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                ✓ Published
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                🕐 Scheduled for{" "}
                {post.scheduledAt ? fmtTime(post.scheduledAt) : "—"}
              </span>
            )}
          </div>
        </div>

        {!isPublished && (
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className="label">Date</label>
              <input
                type="date"
                className="input"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Time</label>
              <input
                type="time"
                className="input"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {!isPublished && (
            <button onClick={handleSave} className="btn-primary flex-1">
              Save Changes
            </button>
          )}
          {!isPublished && (
            <button onClick={onRemove} className="btn-danger">
              Unschedule
            </button>
          )}
          <button onClick={onClose} className="btn-secondary">
            {isPublished ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MonthView ──────────────────────────────────────────────────────────────────
interface MonthViewProps {
  year: number;
  month: number;
  today: Date;
  calendarPosts: Post[];
  brandMap: Record<string, string>;
  onDrop: (target: DropTarget) => void;
  onEventClick: (post: Post) => void;
  onEventRemove: (post: Post) => void;
  onEventDragStart: (post: Post) => void;
}

function MonthView({
  year,
  month,
  today,
  calendarPosts,
  brandMap,
  onDrop,
  onEventClick,
  onEventRemove,
  onEventDragStart,
}: MonthViewProps) {
  const days = getMonthGrid(year, month);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const byDay = groupByDay(calendarPosts);

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-gray-200">
        {WEEKDAYS_SHORT.map((d) => (
          <div
            key={d}
            className="py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide text-center"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const key = day.toDateString();
          const dayPosts = byDay[key] ?? [];
          const isToday = isSameDay(day, today);
          const inMonth = day.getMonth() === month;
          const isOver = dragOver === key;

          return (
            <div
              key={i}
              className={[
                "min-h-[110px] p-1.5 border-b border-r border-gray-100 transition-colors",
                inMonth ? "bg-white" : "bg-gray-50",
                isOver ? "bg-brand-50 ring-1 ring-inset ring-brand-300" : "",
              ].join(" ")}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(key);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                onDrop({ date: day });
              }}
            >
              <p
                className={[
                  "text-xs font-semibold mb-1.5 w-6 h-6 flex items-center justify-center rounded-full",
                  isToday
                    ? "bg-brand-500 text-white"
                    : inMonth
                      ? "text-gray-700"
                      : "text-gray-300",
                ].join(" ")}
              >
                {day.getDate()}
              </p>
              <div className="space-y-0.5">
                {dayPosts.slice(0, 3).map((p) => (
                  <EventPill
                    key={p.postId}
                    post={p}
                    brandName={brandMap[p.brandId]}
                    onClick={() => onEventClick(p)}
                    onRemove={() => onEventRemove(p)}
                    onDragStart={() => onEventDragStart(p)}
                  />
                ))}
                {dayPosts.length > 3 && (
                  <p className="text-[10px] text-gray-400 pl-1">
                    +{dayPosts.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── WeekView ───────────────────────────────────────────────────────────────────
interface WeekViewProps {
  anchor: Date;
  today: Date;
  calendarPosts: Post[];
  brandMap: Record<string, string>;
  onDrop: (target: DropTarget) => void;
  onEventClick: (post: Post) => void;
  onEventRemove: (post: Post) => void;
  onEventDragStart: (post: Post) => void;
}

function WeekView({
  anchor,
  today,
  calendarPosts,
  brandMap,
  onDrop,
  onEventClick,
  onEventRemove,
  onEventDragStart,
}: WeekViewProps) {
  const weekDays = getWeekDays(anchor);
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = 8 * 60;
  }, []);

  const bySlot: Record<string, Post[]> = {};
  for (const p of calendarPosts) {
    if (!p.scheduledAt) continue;
    const d = new Date(p.scheduledAt);
    const di = weekDays.findIndex((wd) => isSameDay(wd, d));
    if (di < 0) continue;
    const key = `${di}-${d.getHours()}`;
    (bySlot[key] ??= []).push(p);
  }

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 300px)", minHeight: 420 }}
    >
      {/* Day headers */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        <div className="w-16 flex-shrink-0" />
        {weekDays.map((d, i) => {
          const isToday = isSameDay(d, today);
          return (
            <div key={i} className="flex-1 py-2 text-center min-w-0">
              <p className="text-xs text-gray-400 uppercase font-medium">
                {WEEKDAYS_SHORT[d.getDay()]}
              </p>
              <p
                className={[
                  "text-sm font-semibold mx-auto w-7 h-7 flex items-center justify-center rounded-full mt-0.5",
                  isToday ? "bg-brand-500 text-white" : "text-gray-700",
                ].join(" ")}
              >
                {d.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div className="flex-1 overflow-y-auto" ref={gridRef}>
        {ALL_HOURS.map((h) => (
          <div
            key={h}
            className="flex border-b border-gray-100"
            style={{ height: 60 }}
          >
            <div className="w-16 flex-shrink-0 pr-2 text-right text-[11px] text-gray-400 select-none -mt-2.5 leading-none">
              {fmtHour(h)}
            </div>
            {weekDays.map((day, di) => {
              const slotKey = `${di}-${h}`;
              const slotPosts = bySlot[slotKey] ?? [];
              const isOver = dragOver === slotKey;
              return (
                <div
                  key={di}
                  className={[
                    "flex-1 border-l border-gray-100 px-0.5 py-0.5 min-w-0 space-y-0.5 transition-colors",
                    isOver ? "bg-brand-50" : "",
                  ].join(" ")}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(slotKey);
                  }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    onDrop({ date: day, hour: h });
                  }}
                >
                  {slotPosts.map((p) => (
                    <EventPill
                      key={p.postId}
                      post={p}
                      showTime={false}
                      brandName={brandMap[p.brandId]}
                      onClick={() => onEventClick(p)}
                      onRemove={() => onEventRemove(p)}
                      onDragStart={() => onEventDragStart(p)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DayView ────────────────────────────────────────────────────────────────────
interface DayViewProps {
  anchor: Date;
  calendarPosts: Post[];
  brandMap: Record<string, string>;
  onDrop: (target: DropTarget) => void;
  onEventClick: (post: Post) => void;
  onEventRemove: (post: Post) => void;
  onEventDragStart: (post: Post) => void;
}

function DayView({
  anchor,
  calendarPosts,
  brandMap,
  onDrop,
  onEventClick,
  onEventRemove,
  onEventDragStart,
}: DayViewProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = 8 * 64;
  }, []);

  const byHour: Record<number, Post[]> = {};
  for (const p of calendarPosts) {
    if (!p.scheduledAt || !isSameDay(new Date(p.scheduledAt), anchor)) continue;
    const h = new Date(p.scheduledAt).getHours();
    (byHour[h] ??= []).push(p);
  }

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 300px)", minHeight: 420 }}
    >
      <div className="flex-1 overflow-y-auto" ref={gridRef}>
        {ALL_HOURS.map((h) => {
          const slotPosts = byHour[h] ?? [];
          const isOver = dragOver === h;
          return (
            <div
              key={h}
              className={[
                "flex border-b border-gray-100 transition-colors",
                isOver ? "bg-brand-50" : "",
              ].join(" ")}
              style={{ minHeight: 64 }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(h);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                onDrop({ date: anchor, hour: h });
              }}
            >
              <div className="w-20 flex-shrink-0 pr-3 text-right text-[11px] text-gray-400 select-none -mt-2.5 leading-none pt-0">
                {fmtHour(h)}
              </div>
              <div className="flex-1 border-l border-gray-100 px-2 py-1 space-y-0.5 min-w-0">
                {slotPosts.map((p) => (
                  <EventPill
                    key={p.postId}
                    post={p}
                    showTime={false}
                    brandName={brandMap[p.brandId]}
                    onClick={() => onEventClick(p)}
                    onRemove={() => onEventRemove(p)}
                    onDragStart={() => onEventDragStart(p)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── FilterSelect ───────────────────────────────────────────────────────────────
function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input text-xs py-1 pl-2 pr-6 min-w-[100px] max-w-[160px]"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────
export default function ContentCalendar() {
  const now = new Date();

  // ─── View / navigation ───────────────────────────────────────────────────────
  const [view, setView] = useState<CalendarView>("month");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [weekAnchor, setWeekAnchor] = useState(now);
  const [dayAnchor, setDayAnchor] = useState(now);

  // ─── Filters ─────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState<FilterMode>("all");
  const [brandFilter, setBrandFilter] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState<string>("");

  // ─── Data ─────────────────────────────────────────────────────────────────────
  const [posts, setPosts] = useState<Post[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  // ─── Paging ───────────────────────────────────────────────────────────────────
  const [cardPage, setCardPage] = useState(0);

  // ─── Modals ───────────────────────────────────────────────────────────────────
  const [scheduleModal, setScheduleModal] = useState<{
    post: Post;
    date: Date;
    hour?: number;
  } | null>(null);
  const [editModal, setEditModal] = useState<Post | null>(null);

  // ─── Drag ref ─────────────────────────────────────────────────────────────────
  const draggingRef = useRef<Post | null>(null);

  // ─── Load posts ───────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.posts.list({ status: "approved", limit: 200 }),
      api.posts.list({ status: "scheduled", limit: 200 }),
      api.posts.list({ status: "published", limit: 200 }),
    ])
      .then(([a, s, p]) => {
        const merged = new Map<string, Post>();
        for (const post of [...a.posts, ...s.posts, ...p.posts])
          merged.set(post.postId, post);
        setPosts([...merged.values()]);
      })
      .catch(console.error);
  }, []);

  // ─── Load brands + campaigns ──────────────────────────────────────────────────
  useEffect(() => {
    api.brands
      .list()
      .then(({ brands: b }) => {
        setBrands(b);
        return Promise.all(b.map((br) => api.campaigns.list(br.brandId)));
      })
      .then((results) => {
        setCampaigns(results.flatMap((r) => r.campaigns));
      })
      .catch(console.error);
  }, []);

  // ─── Derived lookup maps ──────────────────────────────────────────────────────
  const brandMap = Object.fromEntries(brands.map((b) => [b.brandId, b.name]));
  const campaignMap = Object.fromEntries(
    campaigns.map((c) => [c.campaignId, c.name]),
  );

  // Campaigns filtered by selected brand (for the campaign dropdown)
  const dropdownCampaigns = brandFilter
    ? campaigns.filter((c) => c.brandId === brandFilter)
    : campaigns;

  // ─── Navigation ───────────────────────────────────────────────────────────────
  const navigatePrev = () => {
    if (view === "month") {
      if (month === 0) {
        setYear((y) => y - 1);
        setMonth(11);
      } else setMonth((m) => m - 1);
    } else if (view === "week") {
      const d = new Date(weekAnchor);
      d.setDate(d.getDate() - 7);
      setWeekAnchor(d);
    } else {
      const d = new Date(dayAnchor);
      d.setDate(d.getDate() - 1);
      setDayAnchor(d);
    }
  };

  const navigateNext = () => {
    if (view === "month") {
      if (month === 11) {
        setYear((y) => y + 1);
        setMonth(0);
      } else setMonth((m) => m + 1);
    } else if (view === "week") {
      const d = new Date(weekAnchor);
      d.setDate(d.getDate() + 7);
      setWeekAnchor(d);
    } else {
      const d = new Date(dayAnchor);
      d.setDate(d.getDate() + 1);
      setDayAnchor(d);
    }
  };

  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
    setWeekAnchor(t);
    setDayAnchor(t);
  };

  const navLabel = (): string => {
    if (view === "month") return `${MONTHS[month]} ${year}`;
    if (view === "week") {
      const days = getWeekDays(weekAnchor);
      const first = days[0],
        last = days[6];
      const fmt = (d: Date) =>
        d.toLocaleDateString([], { month: "short", day: "numeric" });
      if (first.getMonth() === last.getMonth())
        return `${MONTHS[first.getMonth()]} ${first.getFullYear()}`;
      return `${fmt(first)} – ${fmt(last)}`;
    }
    // Day view – short format fits the toolbar
    return dayAnchor.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ─── Schedule helpers ─────────────────────────────────────────────────────────
  const doSchedule = useCallback(async (post: Post, ts: number) => {
    try {
      await api.posts.schedule(post.postId, ts);
      setPosts((prev) =>
        prev.map((p) =>
          p.postId === post.postId
            ? { ...p, status: "scheduled", scheduledAt: ts }
            : p,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const doUnschedule = useCallback(async (post: Post) => {
    try {
      await api.posts.update(post.postId, {
        scheduledAt: null,
        status: "approved",
      });
      setPosts((prev) =>
        prev.map((p) =>
          p.postId === post.postId
            ? { ...p, status: "approved", scheduledAt: null }
            : p,
        ),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ─── Drop handler ─────────────────────────────────────────────────────────────
  const handleDrop = useCallback(
    ({ date, hour }: DropTarget) => {
      const post = draggingRef.current;
      if (!post) return;
      draggingRef.current = null;

      if (hour !== undefined) {
        // Time-slot drop → schedule immediately at that hour
        const d = new Date(date);
        d.setHours(hour, 0, 0, 0);
        doSchedule(post, d.getTime());
      } else {
        // Day-cell drop → open modal with date pre-filled
        setScheduleModal({ post, date });
      }
    },
    [doSchedule],
  );

  // ─── Filtered post sets ───────────────────────────────────────────────────────
  const applyEntityFilter = (p: Post) => {
    if (brandFilter && p.brandId !== brandFilter) return false;
    if (campaignFilter && p.campaignId !== campaignFilter) return false;
    return true;
  };

  const calendarPosts = posts.filter((p) => {
    if (!p.scheduledAt) return false;
    if (!applyEntityFilter(p)) return false;
    if (filter === "scheduled") return p.status === "scheduled";
    if (filter === "published") return p.status === "published";
    return p.status === "scheduled" || p.status === "published";
  });

  const approvedPosts = posts.filter(
    (p) => p.status === "approved" && !p.scheduledAt && applyEntityFilter(p),
  );
  const totalPages = Math.ceil(approvedPosts.length / CARDS_PER_PAGE);
  const pagedApproved = approvedPosts.slice(
    cardPage * CARDS_PER_PAGE,
    (cardPage + 1) * CARDS_PER_PAGE,
  );

  // Reset card page when filters change
  const handleBrandFilter = (v: string) => {
    setBrandFilter(v);
    setCampaignFilter(""); // reset campaign when brand changes
    setCardPage(0);
  };
  const handleCampaignFilter = (v: string) => {
    setCampaignFilter(v);
    setCardPage(0);
  };

  // Shared event drag start (works for both calendar events and approved cards)
  const startDragging = (post: Post) => {
    draggingRef.current = post;
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Single compact toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 mr-1 shrink-0">
          Content Calendar
        </h1>

        {/* View tabs */}
        <div className="flex items-center p-0.5 bg-gray-100 rounded-md shrink-0">
          {(["month", "week", "day"] as CalendarView[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={[
                "px-2.5 py-1 text-xs rounded font-medium transition-all capitalize",
                view === v
                  ? "bg-white shadow text-gray-900"
                  : "text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {v}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={navigatePrev}
            className="btn-secondary px-2 py-1 text-xs"
          >
            ←
          </button>
          <button onClick={goToday} className="btn-secondary px-2 py-1 text-xs">
            Today
          </button>
          <button
            onClick={navigateNext}
            className="btn-secondary px-2 py-1 text-xs"
          >
            →
          </button>
        </div>

        {/* Period label */}
        <span className="text-sm font-semibold text-gray-700 min-w-[120px] shrink-0">
          {navLabel()}
        </span>

        {/* Push filters to the right on wider screens */}
        <span className="flex-1" />

        {/* Brand filter */}
        {brands.length > 0 && (
          <FilterSelect
            value={brandFilter}
            onChange={handleBrandFilter}
            placeholder="All Brands"
            options={brands.map((b) => ({ value: b.brandId, label: b.name }))}
          />
        )}

        {/* Campaign filter */}
        {dropdownCampaigns.length > 0 && (
          <FilterSelect
            value={campaignFilter}
            onChange={handleCampaignFilter}
            placeholder="All Campaigns"
            options={dropdownCampaigns.map((c) => ({
              value: c.campaignId,
              label: c.name,
            }))}
          />
        )}

        {/* Status filter */}
        <div className="flex items-center p-0.5 bg-gray-100 rounded-md shrink-0">
          {(["all", "scheduled", "published"] as FilterMode[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={[
                "px-2.5 py-1 text-xs rounded font-medium transition-all",
                filter === f
                  ? "bg-white shadow text-gray-900"
                  : "text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {f === "all"
                ? "All"
                : f === "scheduled"
                  ? "Scheduled"
                  : "Published"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Calendar ── */}
      <div className="card overflow-x-auto">
        <div className={view === "day" ? "min-w-[340px]" : "min-w-[560px]"}>
          {view === "month" && (
            <MonthView
              year={year}
              month={month}
              today={now}
              calendarPosts={calendarPosts}
              brandMap={brandMap}
              onDrop={handleDrop}
              onEventClick={(p) => setEditModal(p)}
              onEventRemove={doUnschedule}
              onEventDragStart={startDragging}
            />
          )}
          {view === "week" && (
            <WeekView
              anchor={weekAnchor}
              today={now}
              calendarPosts={calendarPosts}
              brandMap={brandMap}
              onDrop={handleDrop}
              onEventClick={(p) => setEditModal(p)}
              onEventRemove={doUnschedule}
              onEventDragStart={startDragging}
            />
          )}
          {view === "day" && (
            <DayView
              anchor={dayAnchor}
              calendarPosts={calendarPosts}
              brandMap={brandMap}
              onDrop={handleDrop}
              onEventClick={(p) => setEditModal(p)}
              onEventRemove={doUnschedule}
              onEventDragStart={startDragging}
            />
          )}
        </div>
      </div>

      {/* ── Approved Posts Panel ── */}
      <div className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">
              Ready to Schedule
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {approvedPosts.length === 0
                ? "No approved posts waiting"
                : `${approvedPosts.length} approved post${approvedPosts.length !== 1 ? "s" : ""} — drag onto the calendar or click to schedule`}
            </p>
          </div>

          {/* Card paging — only updates this panel, not the calendar */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCardPage((p) => Math.max(0, p - 1))}
                disabled={cardPage === 0}
                className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
              >
                ←
              </button>
              <span className="text-xs text-gray-500 min-w-[40px] text-center">
                {cardPage + 1} / {totalPages}
              </span>
              <button
                onClick={() =>
                  setCardPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={cardPage === totalPages - 1}
                className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
              >
                →
              </button>
            </div>
          )}
        </div>

        {approvedPosts.length === 0 ? (
          <div className="card p-6 text-center">
            <p className="text-sm text-gray-400">
              No approved posts waiting to be scheduled.
            </p>
            <p className="text-xs text-gray-300 mt-1">
              Approve posts from the Review Queue to schedule them here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {pagedApproved.map((p) => (
              <ApprovedCard
                key={p.postId}
                post={p}
                brandName={brandMap[p.brandId]}
                campaignName={campaignMap[p.campaignId]}
                onDragStart={() => startDragging(p)}
                onClick={() => setScheduleModal({ post: p, date: now })}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Schedule Modal ── */}
      {scheduleModal && (
        <ScheduleModal
          post={scheduleModal.post}
          initialDate={scheduleModal.date}
          initialHour={scheduleModal.hour}
          brandName={brandMap[scheduleModal.post.brandId]}
          campaignName={campaignMap[scheduleModal.post.campaignId]}
          onConfirm={async (ts) => {
            await doSchedule(scheduleModal.post, ts);
            setScheduleModal(null);
          }}
          onClose={() => setScheduleModal(null)}
        />
      )}

      {/* ── Edit Modal ── */}
      {editModal && (
        <EditModal
          post={editModal}
          brandName={brandMap[editModal.brandId]}
          campaignName={campaignMap[editModal.campaignId]}
          onSave={async (ts) => {
            await doSchedule(editModal, ts);
            setEditModal(null);
          }}
          onRemove={async () => {
            await doUnschedule(editModal);
            setEditModal(null);
          }}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
}
